import React, { FC, ReactElement, useMemo } from 'react';
import _ from 'lodash';
import { useTranslation } from 'react-i18next';
import { EStatisticType } from '../../constans/report';
import PageSection from '../../HOC/PageSection/PageSection';
import { useFilters } from '../../hooks/useFilters';
import { ITopAndBottomPerformersResults } from '../../services/reports';
import ReportResultsLegend from '../ReportResultsLegend/ReportResultsLegend';
import SectionTabs from '../SectionTabs/SectionTabs';
import Performers from './Performers/Performers';
import { TopAndBottomPerformersCont, Header } from './TopAndBottomPerformers.styles';

interface ITopAndBottomPerformers {
  data?: ITopAndBottomPerformersResults;
  sectionTitle: ReactElement;
}

const TopAndBottomPerformers: FC<ITopAndBottomPerformers> = ({ data, sectionTitle }) => {
  const { currentFilters, applyAndMergeFilters } = useFilters();
  const { t } = useTranslation();
  const getPerformersFilters = useMemo(() => {
    const performers = currentFilters?.performers;
    if (performers) {
      // @ts-ignore
      const performersFilters = JSON.parse(performers);
      return {
        legend: performersFilters?.legend || false,
        mode: performersFilters?.mode || 'teams',
      };
    }
  }, [currentFilters?.performers]);

  const sectionTabs = [
    { name: t('report.topAndBottomPerformers.teams'), value: 'teams' },
    { name: t('report.topAndBottomPerformers.leaders'), value: 'leaders' },
  ];
  const isTeamsPerformers = useMemo(() => {
    return !_.isEmpty(data?.topTeams?.results) || !_.isEmpty(data?.bottomTeams?.results);
  }, [data?.bottomTeams?.results, data?.topTeams?.results]);

  const isLeadersPerformers = useMemo(() => {
    return !_.isEmpty(data?.topLeaders?.results) || !_.isEmpty(data?.bottomLeaders?.results);
  }, [data?.bottomLeaders?.results, data?.topLeaders?.results]);

  const isToggleBtnAvailable = useMemo(() => {
    return isTeamsPerformers && isLeadersPerformers;
  }, [isLeadersPerformers, isTeamsPerformers]);

  const renderPerformers = useMemo(() => {
    const mode = getPerformersFilters?.mode;
    switch (mode) {
      case 'teams':
        return (
          <Performers
            statisticType={EStatisticType.topBottomTeams}
            topData={{
              data: data?.topTeams,
              noData: t('report.topAndBottomPerformers.noTopTeams'),
            }}
            bottomData={{
              data: data?.bottomTeams,
              noData: t('report.topAndBottomPerformers.noBottomTeams'),
            }}
            mostImprovedData={{
              data: data?.mostImprovedTeams,
              noData: t('report.topAndBottomPerformers.noBottomTeams'),
            }}
          />
        );
      case 'leaders':
        return (
          <Performers
            statisticType={EStatisticType.topBottomLeaders}
            topData={{
              data: data?.topLeaders,
              noData: t('report.topAndBottomPerformers.noTopLeaders'),
            }}
            bottomData={{
              data: data?.bottomLeaders,
              noData: t('report.topAndBottomPerformers.noBottomLeaders'),
            }}
            mostImprovedData={{
              data: data?.mostImprovedLeaders,
              noData: t('report.topAndBottomPerformers.noBottomTeams'),
            }}
          />
        );
      default:
        return (
          <Performers
            statisticType={EStatisticType.topBottomTeams}
            topData={{
              data: data?.topTeams,
              noData: t('report.topAndBottomPerformers.noTopTeams'),
            }}
            bottomData={{
              data: data?.bottomTeams,
              noData: t('report.topAndBottomPerformers.noBottomTeams'),
            }}
            mostImprovedData={{
              data: data?.mostImprovedTeams,
              noData: t('report.topAndBottomPerformers.noBottomTeams'),
            }}
          />
        );
    }
  }, [
    getPerformersFilters?.mode,
    data?.bottomLeaders,
    data?.bottomTeams,
    data?.topLeaders,
    data?.topTeams,
    data?.mostImprovedLeaders,
    data?.mostImprovedTeams,
    t,
  ]);

  return (
    <>
      {(isTeamsPerformers || isLeadersPerformers) && (
        <>
          <Header id="Header">
            {sectionTitle}
            {isToggleBtnAvailable && (
              <SectionTabs
                activeTab={getPerformersFilters?.mode || 'teams'}
                setTab={(tab: string) => applyAndMergeFilters('performers', 'mode', tab)}
                tabs={sectionTabs}
              />
            )}
          </Header>
          <PageSection>
            <TopAndBottomPerformersCont id="TopAndBottomPerformersCont">
              <>
                <ReportResultsLegend
                  noBorder
                  description={data?.description}
                  legendExpanded={getPerformersFilters?.legend}
                  expandLegend={(status: boolean) =>
                    applyAndMergeFilters('performers', 'legend', status)
                  }
                />
                {renderPerformers}
              </>
            </TopAndBottomPerformersCont>
          </PageSection>
        </>
      )}
    </>
  );
};

export default TopAndBottomPerformers;
